import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { onAuthStateChange } from "../firebase/auth";
import { getUserPreferences } from "../firebase/enhancedFirestoreService";
import {
  getUserSubscriptionData,
  subscribeUserSubscription,
} from "../firebase/userSubscriptionFirestore";
import {
  deriveSubscriptionState,
  userHasEntitlement,
} from "../services/subscriptionService";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("USD"); // Default currency
  const [marketCurrency, setMarketCurrency] = useState("USD"); // Market currency
  const [portfolioCurrency, setPortfolioCurrency] = useState("USD"); // Portfolio currency
  const [isTtwror, setIsTtwror] = useState(false); // Default to standard calculation mode

  const [subscriptionRaw, setSubscriptionRaw] = useState(null);
  const [subscriptionHydrated, setSubscriptionHydrated] = useState(false);

  const subscription = useMemo(
    () => deriveSubscriptionState(subscriptionRaw),
    [subscriptionRaw]
  );

  const isPremium = subscription.isPremium;

  const hasEntitlement = useCallback(
    (featureId) => userHasEntitlement(subscription, featureId),
    [subscription]
  );

  const refreshSubscription = useCallback(async () => {
    if (!user?.uid) {
      setSubscriptionRaw(null);
      setSubscriptionHydrated(false);
      return;
    }
    try {
      const data = await getUserSubscriptionData(user.uid);
      setSubscriptionRaw(data);
    } finally {
      setSubscriptionHydrated(true);
    }
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      setSubscriptionRaw(null);
      setSubscriptionHydrated(false);
      return;
    }
    setSubscriptionHydrated(false);
    const unsub = subscribeUserSubscription(user.uid, (raw) => {
      setSubscriptionRaw(raw);
      setSubscriptionHydrated(true);
    });
    return () => unsub();
  }, [user?.uid]);

  // Load user preferences from database
  const loadUserPreferences = async (userId) => {
    try {
      const result = await getUserPreferences(userId);
      if (result.success) {
        const prefs = result.data;
        setCurrency(prefs.marketCurrency || "USD");
        setMarketCurrency(prefs.marketCurrency || "USD");
        setPortfolioCurrency(prefs.portfolioCurrency || "USD");
        setIsTtwror(prefs.isTtwror || false);
        console.log("User preferences loaded:", prefs);
      }
    } catch (error) {
      console.error("Error loading user preferences:", error);
    }
  };

  useEffect(() => {
    console.log("AuthContext: Setting up Firebase auth listener...");

    // Set up real Firebase auth state listener
    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      console.log(
        "AuthContext: Firebase auth state changed:",
        firebaseUser ? "User logged in" : "No user"
      );
      setUser(firebaseUser);
      
      // Load user preferences when user logs in
      if (firebaseUser) {
        await loadUserPreferences(firebaseUser.uid);
      } else {
        // Reset to defaults when user logs out
        setCurrency("USD");
        setMarketCurrency("USD");
        setPortfolioCurrency("USD");
        setIsTtwror(false);
        setSubscriptionRaw(null);
        setSubscriptionHydrated(false);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Function to set a mock user for development (keep this for DEV Skip Login)
  const setMockUser = (mockUser) => {
    console.log("AuthContext: Setting mock user:", mockUser);
    setUser(mockUser);
  };

  // Save user preferences to database
  const saveUserPreferences = async (preferences) => {
    if (!user) return { success: false, error: "No user logged in" };
    
    try {
      const { saveUserPreferences: savePrefs } = await import("../firebase/enhancedFirestoreService");
      const result = await savePrefs(user.uid, preferences);
      
      if (result.success) {
        // Update local state
        if (preferences.marketCurrency) {
          setMarketCurrency(preferences.marketCurrency);
          setCurrency(preferences.marketCurrency); // Keep backward compatibility
        }
        if (preferences.portfolioCurrency) {
          setPortfolioCurrency(preferences.portfolioCurrency);
        }
        if (preferences.isTtwror !== undefined) {
          setIsTtwror(preferences.isTtwror);
        }
      }
      
      return result;
    } catch (error) {
      console.error("Error saving user preferences:", error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    setMockUser, // Keep this for development purposes
    currency,
    setCurrency,
    marketCurrency,
    setMarketCurrency,
    portfolioCurrency,
    setPortfolioCurrency,
    isTtwror,
    setIsTtwror,
    saveUserPreferences,
    subscription,
    subscriptionHydrated,
    isPremium,
    hasEntitlement,
    refreshSubscription,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
